import React from 'react';
import { CurriculumLibraryTreeView } from './CurriculumLibraryTreeView';
import { StudentCurriculumTreeView } from './StudentCurriculumTreeView';
import { AddTopicsTreeActions } from './AddTopicsTreeActions';
import { CreateUnitTreeButton } from './CreateUnitTreeButton';
import { LessonsPanel } from '../../LessonsPanel';
import { viewStates } from '../constants';
import { buildLibraryTreeViewProps, buildStudentCurriculumTreeViewProps } from '../utils/treeViewUtils';
import { Label } from '../../../ui/label';

interface CreateTabDesktopLayoutProps {
    selectedTopics: any[];
    expandedCards: Set<string>;
    expandedNodes: Set<string>;
    setExpandedNodes: (nodes: Set<string>) => void;
    curriculumData: any;
    tenantCustomCurriculumLibraryData?: any | null;
    isStudentCurriculumActive: boolean;
    showAddCustomMenu: boolean;
    onOpenCreateUnitWizard?: () => void;
    studentCurriculumData: any;
    setStudentCurriculumData?: (data: any) => void;
    selectedProvider: string;
    backendLessons: any[];
    selectedStudentPassFailThreshold: number | null;
    loadedNodeIds: Set<string>;
    loadingNodeIds: Set<string>;
    loadingTopicDetails: Set<string>;
    lessonsPanelRef: React.RefObject<HTMLDivElement>;
    onTreeNodeSelect: (node: any) => void;
    onLoadNodeChildren: (nodeId: string, nodeType: 'year' | 'topic') => void;
    onRemoveTopic: (index: number) => void;
    onToggleCard: (topicId: string) => void;
    onCreateAssessment: () => void;
    onOpenCreateYourOwnWizard: () => void;
    onRemoveTopicsFromStudentCurriculum: () => void;
    onAddTopics: () => void;
    onDeleteTenantCustomCurriculumTopics?: () => void;
    buildBreadcrumb: (topic: any) => string;
    getProviderName: (providerKey: string | null | undefined) => string;
    handleLoadTopicDetails: (topicId: string, providerKey?: string) => void;
    setViewState: (state: number) => void;
    onClearAll: () => void;
    studentId?: string | null;
    selectedStudentFirstName?: string;
    /** Navigate from instructional unit drill-down into unit panels under Create / Add Topics tabs. */
    onOpenUnitFromPlan?: (unit: any) => void;
}

export function CreateTabDesktopLayout({
    selectedTopics,
    expandedCards,
    expandedNodes,
    setExpandedNodes,
    curriculumData,
    tenantCustomCurriculumLibraryData,
    isStudentCurriculumActive,
    showAddCustomMenu,
    onOpenCreateUnitWizard,
    studentCurriculumData,
    setStudentCurriculumData,
    selectedProvider,
    backendLessons,
    selectedStudentPassFailThreshold,
    loadedNodeIds,
    loadingNodeIds,
    loadingTopicDetails,
    lessonsPanelRef,
    onTreeNodeSelect,
    onLoadNodeChildren,
    onRemoveTopic,
    onToggleCard,
    onCreateAssessment,
    onOpenCreateYourOwnWizard,
    onRemoveTopicsFromStudentCurriculum,
    onAddTopics,
    onDeleteTenantCustomCurriculumTopics,
    buildBreadcrumb,
    getProviderName,
    handleLoadTopicDetails,
    setViewState,
    onClearAll,
    studentId,
    selectedStudentFirstName = '',
    onOpenUnitFromPlan
}: CreateTabDesktopLayoutProps) {
    // Check if student curriculum has no subjects (E180), not the library tree (E179)
    const isStudentCurriculumEmpty =
        isStudentCurriculumActive &&
        (!studentCurriculumData?.e180_Subjects || studentCurriculumData.e180_Subjects.length === 0);
    
    // Show LessonsPanel only if student curriculum is not empty (or if viewing library/provider)
    const shouldShowLessonsPanel = !isStudentCurriculumEmpty;

    const libraryTreeViewProps = buildLibraryTreeViewProps(
        onTreeNodeSelect,
        selectedTopics,
        expandedNodes,
        setExpandedNodes,
        curriculumData,
        studentCurriculumData,
        selectedProvider,
        backendLessons,
        selectedStudentPassFailThreshold,
        onLoadNodeChildren,
        loadedNodeIds,
        loadingNodeIds,
        false, // Don't show hint on desktop (it's shown in LessonsPanel)
        false,
        selectedStudentFirstName,
        studentId ?? null,
        onOpenUnitFromPlan
    );

    const customCurriculumTreeProps =
        tenantCustomCurriculumLibraryData?.e179_Subjects?.length > 0
            ? buildLibraryTreeViewProps(
                  onTreeNodeSelect,
                  selectedTopics,
                  expandedNodes,
                  setExpandedNodes,
                  tenantCustomCurriculumLibraryData,
                  studentCurriculumData,
                  'custom',
                  backendLessons,
                  selectedStudentPassFailThreshold,
                  onLoadNodeChildren,
                  loadedNodeIds,
                  loadingNodeIds,
                  false,
                  true,
                  selectedStudentFirstName,
                  studentId ?? null,
                  onOpenUnitFromPlan
              )
            : null;

    const studentCurriculumTreeViewProps = buildStudentCurriculumTreeViewProps(
        onTreeNodeSelect,
        selectedTopics,
        expandedNodes,
        setExpandedNodes,
        studentCurriculumData,
        backendLessons,
        selectedStudentPassFailThreshold,
        onLoadNodeChildren,
        loadedNodeIds,
        loadingNodeIds,
        studentId,
        selectedStudentFirstName,
        onOpenUnitFromPlan,
        setStudentCurriculumData
    );

    return (
        <div className="hidden lg:flex lg:h-full lg:overflow-hidden">
            <div className="w-auto min-w-80 flex-shrink-0 overflow-hidden border-r border-gray-200 pr-2 relative">
                {isStudentCurriculumActive ? (
                    <div className="flex flex-col h-full">
                        {onOpenCreateUnitWizard && (
                            <div className="pl-2 pr-2 lg:pr-4 pb-2 pt-2 mb-3 flex-shrink-0">
                                <CreateUnitTreeButton
                                    onOpenCreateUnitWizard={onOpenCreateUnitWizard}
                                    selectedTopics={selectedTopics}
                                    studentCurriculumData={studentCurriculumData}
                                    onCreateAssessment={() => {
                                        onCreateAssessment();
                                        setViewState(viewStates.creatingAssessment);
                                    }}
                                    onRemoveTopics={onRemoveTopicsFromStudentCurriculum}
                                />
                            </div>
                        )}
                        <div className="min-h-0 flex-1 overflow-hidden">
                            <StudentCurriculumTreeView {...studentCurriculumTreeViewProps} className="h-full" />
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col h-full">
                        {showAddCustomMenu && (
                        <div className="pl-2 pr-2 lg:pr-4 pb-2 pt-2 mb-3 flex-shrink-0">
                            <AddTopicsTreeActions
                                onOpenCreateYourOwnWizard={onOpenCreateYourOwnWizard}
                                selectedTopics={selectedTopics}
                                onAddTopics={onAddTopics}
                                onDeleteTenantCustomCurriculumTopics={onDeleteTenantCustomCurriculumTopics}
                            />
                        </div>
                        )}
                        {customCurriculumTreeProps && (
                            <div className="shrink-0 border-b border-gray-200 mb-2 pb-2">
                                <Label className="block pl-2 pr-2 lg:pr-4 pb-1 flex-shrink-0 text-lg font-semibold text-[#1a5172]">
                                    Custom curriculum
                                </Label>
                                <div className="min-h-0 max-h-[min(45vh,28rem)] overflow-y-auto">
                                    <CurriculumLibraryTreeView {...customCurriculumTreeProps} className="min-h-0" />
                                </div>
                            </div>
                        )}
                        <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
                            <Label className="block pl-2 pr-2 lg:pr-4 pb-1 flex-shrink-0 text-lg font-semibold text-[#1a5172]">
                                National Curriculum
                            </Label>
                            <div className="min-h-0 flex-1 overflow-hidden">
                                <CurriculumLibraryTreeView {...libraryTreeViewProps} className="h-full" />
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            {shouldShowLessonsPanel && (
                <div className="flex-1 overflow-hidden">
                    <div ref={lessonsPanelRef} className="h-full bg-white">
                        <LessonsPanel
                            selectedTopics={selectedTopics.filter(topic => {
                                const childTopics = topic.e179_ChildTopics || topic.e180_ChildTopics || [];
                                return childTopics.length === 0;
                            })}
                            expandedCards={expandedCards}
                            onRemoveTopic={onRemoveTopic}
                            onToggleCard={onToggleCard}
                            onClearAll={onClearAll}
                            buildBreadcrumb={buildBreadcrumb}
                            isStudentCurriculum={isStudentCurriculumActive}
                            studentCurriculumData={studentCurriculumData}
                            getProviderName={getProviderName}
                            units={backendLessons}
                            passFailThreshold={selectedStudentPassFailThreshold}
                            onLoadTopicDetails={handleLoadTopicDetails}
                            loadingTopicDetails={loadingTopicDetails}
                            curriculumProviderKey={selectedProvider}
                            studentId={studentId}
                            selectedStudentFirstName={selectedStudentFirstName}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
